import { hasRichTextContent } from '../../../../../helpers/textHelpers';
import { Optional, TextEditorContent } from '../../../../../types';
import { Detail, Plan } from '../types';

// A localisation subtree as the websheet receives it: string copy, rich-text
// bodies and the odd boolean flag share one bag, so each resolver narrows
// what it hands back.
type LocalisationText = Record<string, unknown>;

/**
 * Resolve the plan-selection localisation text for the watch activation screen.
 *
 * In 'linked' (sa-watch / Family Plan) mode the section is driven by the carrier's
 * `family_plan_*` localisation fields, falling back to the standard plan-selection
 * fields when a Family Plan field is not configured. In 'paired' (or missing) mode
 * the standard fields are always used.
 */
export const resolvePlanSelectionText = (
  isLinked: boolean,
  text: Record<string, unknown> | undefined,
) => {
  const familyPlan = (field: string) => {
    return (isLinked ? (text?.[`family_plan_${field}`] as string) : '') || '';
  };

  return {
    title: familyPlan('title') || (text?.title as string | undefined),
    content:
      (isLinked ? text?.family_plan_content : undefined) ?? text?.content,
    continue: familyPlan('continue') || (text?.continue as string | undefined),
    // The wizard mirror generates the twin off the base key (`checkboxLabel`);
    // `family_plan_checkbox` is the pre-mirror key kept for saved configs.
    checkboxLabel:
      familyPlan('checkboxLabel') ||
      familyPlan('checkbox') ||
      (text?.checkboxLabel as string | undefined),
    link: familyPlan('link') || (text?.link as string | undefined),
  };
};

/**
 * Resolve the Terms & Conditions localisation text for the watch T&C view.
 *
 * In 'linked' (sa-watch / Family Plan) mode the T&C copy is driven by the carrier's
 * `family_plan_*` fields, falling back to the standard T&C fields when a Family Plan
 * field is not configured. In 'paired' (or missing) mode the standard fields are used.
 */
export const resolveTermsText = (
  isLinked: boolean,
  tc: Record<string, unknown> | undefined,
) => {
  const familyPlan = (field: string) => {
    return (isLinked ? (tc?.[`family_plan_${field}`] as string) : '') || '';
  };

  return {
    title: familyPlan('title') || (tc?.title as string | undefined),
    content: (isLinked ? tc?.family_plan_content : undefined) ?? tc?.content,
    back_button:
      familyPlan('back_button') || (tc?.back_button as string | undefined),
  };
};

/**
 * Resolve the watch-transfer form copy (title, consent body, feedback titles and
 * paragraphs) for the given subscription type.
 *
 * In 'linked' (sa-watch / standalone) mode each field prefers its
 * `family_plan_<key>` localisation variant, falling back to the standard (paired)
 * key when the linked variant is not configured. In 'paired' (or missing) mode the
 * standard keys are always used, so existing Multi-SIM journeys are unchanged.
 *
 * Both the flat portal-preview `text` object and the resolved `transfer_t&c`
 * localisation subtree are searched, matching the component's existing lookup
 * chain. `parentKey` mirrors the legacy nested lookup for the form-level fields.
 */
const buildTransferPicker = (
  isLinked: boolean,
  text: LocalisationText | undefined,
  loc: LocalisationText | undefined,
) => {
  return function pick<T = string>(
    key: string,
    parentKey?: string,
  ): T | undefined {
    const get = (lookup: string): unknown => {
      // Runtime hands us the already-resolved `transfer_t&c` subtree, where
      // every field is FLAT — the nested `parentKey` shape only exists in
      // legacy/preview payloads. Check nested first, then fall back flat.
      const nested = parentKey
        ? (loc?.[parentKey] as LocalisationText | undefined)?.[lookup]
        : undefined;
      return text?.[lookup] || nested || loc?.[lookup];
    };

    if (isLinked) {
      const linked = get(`family_plan_${key}`);
      if (linked) {
        return linked as T;
      }
    }

    return get(key) as T | undefined;
  };
};

export const resolveTransferText = (
  isLinked: boolean,
  text: LocalisationText | undefined,
  loc: LocalisationText | undefined,
) => {
  const pick = buildTransferPicker(isLinked, text, loc);

  return {
    title: pick('watch_plan_display_title', 'transfer_t&c'),
    plansText: pick('watch_transfer_plans_text', 'transfer_t&c'),
    submittingTitle: pick('watch_transfer_submitting_title'),
    successTitle: pick('watch_transfer_success_title'),
    submittingParagraph: pick('watch_transfer_submitting_paragraph'),
    successParagraph: pick('watch_transfer_success_paragraph'),
    closeButton: pick('close_button'),
    // Transfer form consent copy: continue button + T&C acknowledgment line.
    // Same base names as the watch tab so the wizard mirror generates the
    // family_plan_ twins without special-casing.
    continueButton: pick('continue', 'transfer_t&c'),
    checkboxLabel: pick('checkboxLabel', 'transfer_t&c'),
    link: pick('link', 'transfer_t&c'),
    // Truthy-based like every other pick: a linked flag saved as `false`
    // falls back to the base flag. Acceptable — the flag only *hides* the
    // description when true, so falsy resolves to "show" either way.
    disablePlanDescription: pick<boolean>(
      'watch_transfer_disable_plan_description',
      'transfer_t&c',
    ),
  };
};

/**
 * Resolve the watch-transfer Terms & Conditions copy (title, content, back button)
 * for the given subscription type. Linked mode prefers `family_plan_<key>`, falling
 * back to the standard key; paired (or missing) mode uses the standard keys.
 */
export const resolveTransferTermsText = (
  isLinked: boolean,
  tc: LocalisationText | undefined,
) => {
  function pick<T = string>(key: string): T | undefined {
    const linked = isLinked ? tc?.[`family_plan_${key}`] : undefined;
    return (linked ?? tc?.[key]) as T | undefined;
  }

  return {
    title: pick('title'),
    content: pick<TextEditorContent[]>('content'),
    watch_transfer_watch_button: pick('watch_transfer_watch_button'),
  };
};

/**
 * Resolve a Watch Management (manage tab) landing/actions/loading/success/
 * confirmation copy field for the given subscription type.
 *
 * In 'linked' (sa-watch / Family Plan) mode the field prefers its
 * `family_plan_<key>` localisation variant, falling back to the standard
 * (paired) key when the linked variant is not configured. In 'paired' (or
 * missing) mode the standard key is always used.
 */
export function resolveManageText<T = string>(
  key: string,
  isLinked: boolean,
  text?: LocalisationText,
): T | undefined {
  return ((isLinked ? text?.[`family_plan_${key}`] : undefined) ||
    text?.[key]) as T | undefined;
}

/**
 * Resolve the Watch Management error screen's `error_title` / `close_button`
 * copy for the given subscription type.
 *
 * Manage owns its own `manage_error_states` fields, so the lookup prefers
 * manage's own (family-first) copy first, then falls back to the legacy
 * `text.watch` namespace (family-first there too) for configs saved before
 * this change — never rendering blank.
 */
export const resolveManageErrorText = (
  key: 'error_title' | 'close_button',
  isLinked: boolean,
  text?: LocalisationText,
): string | undefined => {
  const watch = text?.watch as LocalisationText | undefined;

  return ((isLinked ? text?.[`family_plan_${key}`] : undefined) ||
    text?.[key] ||
    (isLinked ? watch?.[`family_plan_${key}`] : undefined) ||
    watch?.[key]) as string | undefined;
};

/**
 * Build the `localisation` object passed to `DefaultErrorTemplate` for the
 * Watch Management error screen.
 *
 * Spreads the legacy `text.watch` namespace first, then manage's own (flat)
 * fields on top, so manage-own values win whenever the carrier's stored
 * config actually has that key. Unmigrated configs lack the manage-own key
 * entirely (not `undefined`), so this never shadows the legacy value.
 */
export const buildManageErrorLocalisation = (
  text?: LocalisationText,
): LocalisationText => {
  return {
    ...(text?.watch as LocalisationText | undefined),
    ...text,
  };
};

/**
 * Find the plan detail matching `currentLocale` (exact, `lang_country`, or
 * `lang-` prefix), falling back to the first detail, then `{}`.
 *
 * BE serializes with `response_model_exclude_none`, so a detail's Optional
 * `locale` can be absent entirely — such details are skipped, never matched.
 */
export const findPlanDetails = (
  plan: Plan | null | undefined,
  currentLocale: Optional<string>,
): Partial<Detail> => {
  const { details } = plan || {};

  const planDetails = currentLocale
    ? details?.find?.(({ locale }) => {
        if (!locale) {
          return false;
        }
        const [language, country] = currentLocale.split('-');
        const localeLowerCased = locale.toLowerCase();
        return (
          localeLowerCased === currentLocale.toLowerCase() ||
          localeLowerCased === `${language}_${country}` ||
          localeLowerCased.startsWith(`${language}-`)
        );
      })
    : undefined;

  if (planDetails) {
    return planDetails;
  }

  return details?.[0] || {};
};

/**
 * Resolve the contract-page body for the watch activation screen.
 *
 * In 'linked' (sa-watch / Family Plan) mode the `family_plan_contract_content`
 * twin wins when it carries text, falling back to the base `contract_content`
 * otherwise. The wizard saves a twin the carrier left blank as an empty editor
 * value, and an empty array is truthy, so a plain `||` fallback would keep the
 * blank twin. In 'paired' (or missing) mode the base key is always used.
 * Whether the result has content is what decides if the contract page exists.
 */
export const resolveContractContent = (
  isLinked: boolean,
  text: Record<string, unknown> | undefined,
): Optional<TextEditorContent[]> => {
  const familyPlanContent = isLinked
    ? (text?.family_plan_contract_content as Optional<TextEditorContent[]>)
    : undefined;

  if (hasRichTextContent(familyPlanContent)) {
    return familyPlanContent;
  }

  return text?.contract_content as Optional<TextEditorContent[]>;
};
